import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import './SchoolDashboard.css';
import './BusSeatManagement.css';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';

function BusSeatManagement() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [seatAssignments, setSeatAssignments] = useState({}); // {seatNumber: studentId}
  const [loadingSeats, setLoadingSeats] = useState(false);
  const [students, setStudents] = useState([]);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const sd = localStorage.getItem('schoolData');
    if (!sd) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(sd));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchVehicles();
      fetchStudents();
    }
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    setSelectedVehicle(null);
    setSeatAssignments({});
  }, [selectedBranchId]);

  useEffect(() => {
    if (selectedVehicle) {
      fetchSeatAssignments();
    }
  }, [selectedVehicle]);

  const fetchVehicles = async () => {
    try {
      setLoadingVehicles(true);
      let query = supabase
        .from('transport_vehicles')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('bus_number');

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching vehicles:', error);
        return;
      }

      setVehicles(data || []);
      if (data && data.length > 0 && !selectedVehicle) {
        setSelectedVehicle(data[0]);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingVehicles(false);
    }
  };

  const fetchStudents = async () => {
    try {
      setLoadingStudents(true);
      let query = supabase
        .from('students')
        .select('id, first_name, middle_name, last_name, transport_service_enabled, route_id, bus_seat_number')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('first_name');

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching students:', error);
        return;
      }

      setStudents(data || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingStudents(false);
    }
  };

  const fetchSeatAssignments = async () => {
    if (!selectedVehicle) return;

    try {
      setLoadingSeats(true);
      // Fetch students assigned to this vehicle's routes
      let routesQuery = supabase
        .from('routes')
        .select('id')
        .eq('vehicle_id', selectedVehicle.id)
        .eq('is_active', true);

      if (selectedBranchId != null) {
        routesQuery = routesQuery.eq('branch_id', selectedBranchId);
      }

      const { data: routes, error: routesError } = await routesQuery;

      if (routesError) {
        console.error('Error fetching routes:', routesError);
        return;
      }

      const routeIds = routes.map(r => r.id);

      if (routeIds.length === 0) {
        setSeatAssignments({});
        setLoadingSeats(false);
        return;
      }

      // Fetch students with this vehicle or route
      let studentsQuery = supabase
        .from('students')
        .select('id, first_name, middle_name, last_name, bus_seat_number, route_id')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .eq('transport_service_enabled', true)
        .in('route_id', routeIds.length > 0 ? routeIds : [0]);

      if (selectedBranchId != null) {
        studentsQuery = studentsQuery.eq('branch_id', selectedBranchId);
      }

      const { data: assignedStudents, error: studentsError } = await studentsQuery;

      if (studentsError) {
        console.error('Error fetching assigned students:', studentsError);
        return;
      }

      // Build seat assignments map
      const assignments = {};
      if (assignedStudents) {
        assignedStudents.forEach(student => {
          if (student.bus_seat_number && student.bus_seat_number > 0) {
            assignments[student.bus_seat_number] = {
              studentId: student.id,
              studentName: `${student.first_name} ${student.middle_name ? student.middle_name + ' ' : ''}${student.last_name}`
            };
          }
        });
      }

      setSeatAssignments(assignments);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingSeats(false);
    }
  };

  const handleSeatClick = async (seatNumber) => {
    if (!selectedVehicle) return;
    
    setSelectedSeat(seatNumber);
    
    // Get routes for this vehicle
    const { data: routes, error: routesError } = await supabase
      .from('routes')
      .select('id')
      .eq('vehicle_id', selectedVehicle.id)
      .eq('is_active', true);

    if (routesError) {
      console.error('Error fetching routes:', routesError);
      return;
    }

    const routeIds = routes ? routes.map(r => r.id) : [];
    console.log('Route IDs for vehicle:', routeIds);
    console.log('All students:', students);

    // Filter students who are on routes for this vehicle and don't have a seat
    const available = students.filter(student => {
      if (!student.transport_service_enabled) {
        console.log(`Student ${student.id} - transport not enabled`);
        return false;
      }
      if (!student.route_id) {
        console.log(`Student ${student.id} - no route_id`);
        return false;
      }
      if (!routeIds.includes(student.route_id)) {
        console.log(`Student ${student.id} - route_id ${student.route_id} not in routeIds`);
        return false;
      }
      // Check if student is already assigned to a seat
      const alreadyAssigned = Object.values(seatAssignments).some(
        assignment => assignment.studentId === student.id
      );
      if (alreadyAssigned) {
        console.log(`Student ${student.id} - already assigned to a seat`);
        return false;
      }
      console.log(`Student ${student.id} - available:`, student);
      return true;
    });

    console.log('Available students:', available);
    setAvailableStudents(available);
    setShowStudentModal(true);
  };

  const handleAssignStudent = async (studentId) => {
    if (!selectedVehicle || !selectedSeat) return;

    try {
      // Update student's bus_seat_number
      const { error } = await supabase
        .from('students')
        .update({ bus_seat_number: selectedSeat })
        .eq('id', studentId);

      if (error) {
        console.error('Error assigning seat:', error);
        alert('Error assigning seat. Please try again.');
        return;
      }

      // Get student name for success message
      const assignedStudent = availableStudents.find(s => s.id === studentId);
      const studentName = assignedStudent 
        ? `${assignedStudent.first_name} ${assignedStudent.middle_name ? assignedStudent.middle_name + ' ' : ''}${assignedStudent.last_name}`.trim()
        : 'Student';

      // Refresh seat assignments
      await fetchSeatAssignments();
      setShowStudentModal(false);
      setSelectedSeat(null);
      
      // Show success popup
      setSuccessMessage(`${studentName} has been assigned to Seat ${selectedSeat}!`);
      setShowSuccessPopup(true);
      
      // Auto-hide after 3 seconds
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (err) {
      console.error('Error:', err);
      alert('Error assigning seat. Please try again.');
    }
  };

  const handleRemoveSeat = async (seatNumber) => {
    if (!selectedVehicle || !window.confirm('Are you sure you want to remove this seat assignment?')) {
      return;
    }

    try {
      const assignment = seatAssignments[seatNumber];
      if (!assignment) return;

      // Remove seat assignment from student
      const { error } = await supabase
        .from('students')
        .update({ bus_seat_number: null })
        .eq('id', assignment.studentId);

      if (error) {
        console.error('Error removing seat assignment:', error);
        alert('Error removing seat assignment. Please try again.');
        return;
      }

      // Get student name for success message
      const removedStudentName = assignment.studentName || 'Student';
      
      // Refresh seat assignments
      await fetchSeatAssignments();
      
      // Show success popup
      setSuccessMessage(`${removedStudentName} has been removed from Seat ${seatNumber}!`);
      setShowSuccessPopup(true);
      
      // Auto-hide after 3 seconds
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (err) {
      console.error('Error:', err);
      alert('Error removing seat assignment. Please try again.');
    }
  };

  const renderSeats = () => {
    if (!selectedVehicle) return null;

    const capacity = selectedVehicle.capacity || 0;
    const seats = [];
    const seatsPerRow = 4; // 2 seats on each side

    for (let i = 1; i <= capacity; i++) {
      const isOccupied = seatAssignments[i];
      const row = Math.floor((i - 1) / seatsPerRow);
      const col = (i - 1) % seatsPerRow;

      if (col === 0) {
        seats.push([]);
      }

      seats[row].push({
        number: i,
        occupied: !!isOccupied,
        student: isOccupied ? isOccupied.studentName : null,
        studentId: isOccupied ? isOccupied.studentId : null
      });
    }

    return (
      <div className="seats-container">
        <div className="seats-header">
          <h3>Bus Seats - {selectedVehicle.bus_number}</h3>
          <div className="seats-stats">
            <span className="stat-item">
              Total: <strong>{capacity}</strong>
            </span>
            <span className="stat-item">
              Occupied: <strong>{Object.keys(seatAssignments).length}</strong>
            </span>
            <span className="stat-item">
              Available: <strong>{capacity - Object.keys(seatAssignments).length}</strong>
            </span>
          </div>
        </div>

        <div className="seats-grid">
          {seats.map((row, rowIndex) => (
            <div key={rowIndex} className="seat-row">
              {row.map((seat, seatIndex) => {
                const isLeft = seatIndex < 2;
                return (
                  <div
                    key={seat.number}
                    className={`seat ${seat.occupied ? 'occupied' : 'available'} ${isLeft ? 'left' : 'right'}`}
                    onClick={() => !seat.occupied && handleSeatClick(seat.number)}
                    title={seat.occupied ? `Occupied by: ${seat.student}` : `Seat ${seat.number} - Click to assign`}
                  >
                    {!seat.occupied && <div className="seat-number">{seat.number}</div>}
                    {seat.occupied && (
                      <div className="seat-student">
                        <div className="student-initials">
                          {seat.student
                            .split(' ')
                            .map(n => n[0])
                            .join('')
                            .toUpperCase()
                            .substring(0, 2)}
                        </div>
                        <div className="student-name">{seat.student}</div>
                        <button
                          className="remove-seat-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveSeat(seat.number);
                          }}
                          title="Remove assignment"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {row.length < seatsPerRow && (
                <div className="seat-aisle">
                  <div className="aisle-label">Aisle</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolSidebar schoolData={schoolData} />
      <SchoolMobileHeader 
        schoolData={schoolData} 
        onMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} 
      />

      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu-dropdown" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <h2>Menu</h2>
              <button className="mobile-menu-close" onClick={() => setMobileMenuOpen(false)}>✕</button>
            </div>
            <div className="mobile-menu-tabs">
              <button 
                className={`mobile-menu-tab ${true ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/bus-seat-management');
                  setMobileMenuOpen(false);
                }}
              >
                Bus Seat Management
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="main-content">
        <div className="content-header desktop-only">
          <h1>Bus Seat Management</h1>
          <div className="user-info">
            <span>{schoolData?.school_name || 'School'}</span>
            <div className="user-avatar">🏫</div>
          </div>
        </div>

      <div className="content-body">
        <div className="branch-filter-row">
          <BranchSelector
            schoolId={schoolData?.id}
            selectedBranchId={selectedBranchId}
            onBranchChange={handleBranchChange}
          />
          <span className="branch-context-text">
            {selectedBranchId
              ? 'Showing seats for the selected branch'
              : 'Showing seats across all branches'}
          </span>
        </div>
        <div className="bus-seat-management">
            <div className="vehicle-selector">
              <label htmlFor="vehicle-select">Select Vehicle:</label>
              <select
                id="vehicle-select"
                value={selectedVehicle?.id || ''}
                onChange={(e) => {
                  const vehicle = vehicles.find(v => v.id === parseInt(e.target.value));
                  setSelectedVehicle(vehicle);
                }}
                className="vehicle-select"
              >
                <option value="">-- Select a vehicle --</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.bus_number} ({vehicle.capacity} seats)
                  </option>
                ))}
              </select>
            </div>

            {loadingSeats ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading seat assignments...</p>
              </div>
            ) : selectedVehicle ? (
              renderSeats()
            ) : (
              <div className="no-vehicle-selected">
                <p>Please select a vehicle to view and manage seats.</p>
              </div>
            )}

            {showStudentModal && (
              <div className="modal-overlay" onClick={() => setShowStudentModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>Assign Student to Seat {selectedSeat}</h3>
                    <button className="modal-close" onClick={() => setShowStudentModal(false)}>✕</button>
                  </div>
                  <div className="modal-body">
                    {availableStudents.length === 0 ? (
                      <div className="no-students-message">
                        <p>No available students found for this vehicle.</p>
                        <p style={{ fontSize: '14px', color: '#6B7280', marginTop: '8px' }}>
                          Make sure students have transport service enabled and are assigned to routes for this vehicle.
                        </p>
                      </div>
                    ) : (
                      <div className="student-list">
                        {availableStudents.map(student => {
                          const fullName = `${student.first_name || ''} ${student.middle_name ? student.middle_name + ' ' : ''}${student.last_name || ''}`.trim();
                          const displayName = fullName || `Student ID: ${student.id}`;
                          return (
                            <div
                              key={student.id}
                              className="student-item"
                              onClick={() => handleAssignStudent(student.id)}
                            >
                              <div className="student-info">
                                <div className="modal-student-name">
                                  {displayName}
                                </div>
                              </div>
                              <button 
                                className="assign-btn" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssignStudent(student.id);
                                }}
                              >
                                Assign
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Success Popup */}
            {showSuccessPopup && (
              <div className="success-popup-overlay" onClick={() => setShowSuccessPopup(false)}>
                <div className="success-popup" onClick={(e) => e.stopPropagation()}>
                  <div className="success-popup-icon">✓</div>
                  <div className="success-popup-content">
                    <h3 className="success-popup-title">Success!</h3>
                    <p className="success-popup-message">{successMessage}</p>
                  </div>
                  <button 
                    className="success-popup-close"
                    onClick={() => setShowSuccessPopup(false)}
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BusSeatManagement;

